import { fetchEventSource } from '@microsoft/fetch-event-source';

import STORE from '@/store';
import { isTV } from '@/util/product-switch';

import { addCommonHeader } from '..';
import type { Issues } from '../alarm/types';
import iAxios from '../IAxios';
import api from './api';
import type * as apiType from './types';

export const generateToken = (data) =>
  iAxios.request<{ token: string }>({
    url: api.generateToken,
    method: 'post',
    data: {
      ...data,
      platform_id: 1,
      start: 0,
      tab: 0,
      version: 2,
      limit: 1,
    },
  });

export const analysisToken = (params) =>
  iAxios.request({
    url: api.analysisToken,
    method: 'get',
    params,
  });

export const getGroupFields = (data: apiType.GetGroupFieldsReq) =>
  iAxios.request<apiType.GetGroupFieldsRsp>({
    url: api.getGroupFields,
    method: 'post',
    data,
  });

/**
 * 根据 filters 判断是否存在 _id 过滤条件
 * 如果存在 _id 且有值，返回 false（condition_tag: false）
 * 否则返回 true（condition_tag: true）
 */
const getConditionTag = (filters?: any[]): boolean => {
  if (!filters || !Array.isArray(filters)) {
    return true;
  }
  const hasIdFilter = filters.some(
    (item) =>
      (item?.attribute_name === '_id' || item?.key === '_id') &&
      item?.filter_content
  );
  return !hasIdFilter;
};

export const getFeedbackListV2Aisee = (data) => {
  const cData = {
    version: 2,
    platform_id: 1,
    tab: 0,
    ...data,
    condition_tag: getConditionTag(data?.filters),
  };
  // 性能优化相关，custom过大
  if (isTV(STORE) && cData.limit > 50) {
    cData.exclusions = ['msgInfo.custom'];
  }
  return iAxios.request({
    url: api.getFeedbackListV2Aisee,
    method: 'post',
    data: cData,
  });
};

/** ai回复固定参数 */
export const AI_REPLY_FIELD = [
  {
    attribute_name: 'msgInfo.contentType',
    filter_content: '1',
    filter_relation: 7,
    type: 2,
  },
];

/** 转人工工单固定参数 */
export const WORK_ORDER_FIELD = [
  {
    attribute_name: 'msgInfo.contentType',
    filter_content: '2',
    filter_relation: 7,
    type: 2,
  },
];

/** ai客服会话列表 */
export const getAiReplyList = (data) => {
  const cData = {
    version: 2,
    platform_id: 1,
    tab: 0,
    ...data,
    filters: [...(data?.filters || []), ...AI_REPLY_FIELD],
  };
  return iAxios.request({
    url: api.getFeedbackListV2Aisee,
    method: 'post',
    data: cData,
  });
};

/** 转人工工单列表 */
export const getWorkOrderList = (data) => {
  const cData = {
    version: 2,
    platform_id: 1,
    tab: 0,
    ...data,
    filters: [...(data?.filters || []), ...WORK_ORDER_FIELD],
  };
  return iAxios.request({
    url: api.getFeedbackListV2Aisee,
    method: 'post',
    data: cData,
  });
};

export const getFeedbackReplyList = (data) => {
  return iAxios.request({
    url: api.getFeedbackReplyList,
    method: 'post',
    data,
  });
};

export const getBusinessFeedbackListV2Aisee = (data) => {
  const cData = { ...data, version: 2, platform_id: 1, tab: 0 };
  return iAxios.request({
    url: api.getBusinessFeedbackListV2Aisee,
    method: 'post',
    data: cData,
  });
};

export const getFeedbackListTotal = (data) => {
  const cData = {
    version: 2,
    platform_id: 1,
    tab: 0,
    ...data,
    condition_tag: getConditionTag(data?.filters),
  };
  return iAxios.request({
    url: api.getFeedbackListTotal,
    method: 'post',
    data: cData,
  });
};

/** 获取ai回复总数 */
export const getAIFeedbackListTotal = (data) => {
  const cData = {
    version: 2,
    platform_id: 1,
    tab: 0,
    ...data,
    filters: [...(data?.filters || []), ...AI_REPLY_FIELD],
  };
  return iAxios.request({
    url: api.getFeedbackListTotal,
    method: 'post',
    data: cData,
  });
};

export const getFeedbackUserCount = (data) => {
  const cData = {
    ...data,
    version: 2,
    platform_id: 1,
    tab: 0,
    condition_tag: getConditionTag(data?.filters),
  };
  return iAxios.request({
    url: api.getFeedbackUserCount,
    method: 'post',
    data: cData,
  });
};

/** ai回复用户数量 */
export const getAIFeedbackUserCount = (data) => {
  const cData = {
    ...data,
    version: 2,
    platform_id: 1,
    tab: 0,
    filters: [...(data?.filters || []), ...AI_REPLY_FIELD],
  };
  return iAxios.request({
    url: api.getFeedbackUserCount,
    method: 'post',
    data: cData,
  });
};

/** 转人工工单总数 */
export const getWorkOrderListTotal = (data) => {
  const cData = {
    version: 2,
    platform_id: 1,
    tab: 0,
    ...data,
    filters: [...(data?.filters || []), ...WORK_ORDER_FIELD],
  };
  return iAxios.request({
    url: api.getFeedbackListTotal,
    method: 'post',
    data: cData,
  });
};

/** 转人工工单用户数量 */
export const getWorkOrderUserCount = (data) => {
  const cData = {
    ...data,
    version: 2,
    platform_id: 1,
    tab: 0,
    filters: [...(data?.filters || []), ...WORK_ORDER_FIELD],
  };
  return iAxios.request({
    url: api.getFeedbackUserCount,
    method: 'post',
    data: cData,
  });
};

export const getFixedFieldOptions = () =>
  iAxios.request<apiType.FeedbackFieldOptionsResponse>({
    url: api.getFixedFieldOptions,
    method: 'post',
    data: { product_id: STORE.productId },
  });

export const getFeedbackWebDetailsAisee = (fid: string) => {
  const params = { fid, app_id: STORE.appId };
  return iAxios.request({
    url: api.getFeedbackWebDetailsAisee,
    method: 'get',
    params,
  });
};

export const feedbackReplys = (data) =>
  iAxios.request({
    url: api.feedbackReplys,
    method: 'post',
    data,
    requireFieldCheck: { fields: ['fid'], return: { replys: [] } },
  });

/** 获取打分标签映射 */
export const getScoreConfig = (params) =>
  iAxios.request<{
    reasons: { reason: string; title: string }[];
    score: number;
  }>({
    url: api.getScoreConfig,
    method: 'get',
    params,
  });

export const sendRecallAisee = (data) =>
  iAxios.request({
    url: api.withdrawReplyAisee,
    method: 'post',
    data,
  });

export const replyCountAisee = (params) =>
  iAxios.request({
    url: api.replyCountAisee,
    method: 'get',
    params,
  });

export const sendReplyAisee = (replyInfo) => {
  const data = { app_id: STORE.appId, reply_info: replyInfo };
  return iAxios.request({
    url: api.sendReplyAisee,
    method: 'post',
    data,
  });
};

export const sendRepliesAisee = (replyInfo, fids) => {
  const data = { app_id: STORE.appId, reply_info: replyInfo, fids };
  return iAxios.request({
    url: api.sendRepliesAisee,
    method: 'post',
    data,
  });
};

export const queryFaqGroupList = (data) =>
  iAxios.request({
    url: api.faqGroupList,
    method: 'post',
    data,
  });

export const getPluginListAisee = (data) =>
  iAxios.request({
    url: api.getPluginListAisee,
    method: 'post',
    data,
  });

export const pluginArtificial = (data) =>
  iAxios.request({
    url: api.pluginArtificial,
    method: 'post',
    data,
  });

export const getHistories = (data) =>
  iAxios.request<{ histories: any[] }>({
    url: api.getHistories,
    method: 'post',
    data,
  });

export const sendFeedbackEdit = (data) =>
  iAxios.request({
    url: api.seedFeedbackEdit,
    method: 'post',
    data,
  });

export const getFeedbackTypeEdit = (data) =>
  iAxios.request({
    url: api.getFeedbackTypeEdit,
    method: 'post',
    data,
  });

export const getLevelTree = (data) =>
  iAxios.request({
    url: api.getLevelTree,
    method: 'post',
    data,
  });

export const getTags = () =>
  iAxios.request({
    url: api.getTags,
    method: 'post',
    data: {
      app_id: STORE.appId,
      limit: 99999,
      start: 0,
    },
  });

export const operateFavorite = (data) =>
  iAxios.request({
    url: api.favorite,
    method: 'post',
    data,
  });

export const getCustomList = (params) =>
  iAxios.request({
    url: api.getCustomList,
    method: 'get',
    params,
  });

export const triggerInBatches = (data) =>
  iAxios.request({
    url: api.triggerInBatches,
    method: 'post',
    data,
  });

export const triggerInBatchesResult = (params) =>
  iAxios.request({
    url: api.triggerInBatchesResult,
    method: 'get',
    params,
  });

export const getBusinessTypes = (data) =>
  iAxios.request({
    url: api.getBusinessTypes,
    method: 'post',
    data,
  });

export const getIssueListAisee = (data) =>
  iAxios.request<Issues>({
    url: api.getIssueListAisee,
    method: 'post',
    data,
  });

export const getIssueDetail = (data) =>
  iAxios.request<any>({
    url: api.getIssueDetail,
    method: 'post',
    data,
  });

export const delFeedbackRelate = (data) =>
  iAxios.request({
    url: api.delFeedbackRelate,
    method: 'delete',
    data,
  });

export const dimensionAttribution = (data) =>
  iAxios.request({
    url: api.dimensionAttribution,
    method: 'post',
    data,
  });

export const getAiseeIssueHistory = (data) =>
  iAxios.request({
    url: api.getAiseeIssueHistory,
    method: 'post',
    data,
  });

export const addAiseeIssueComment = (data) =>
  iAxios.request({
    url: api.addAiseeIssueComment,
    method: 'post',
    data,
  });

export const getAiseeIssueComments = (data) =>
  iAxios.request({
    url: api.getAiseeIssueComments,
    method: 'post',
    data,
  });

export const saveIssue = (data) =>
  iAxios.request({
    url: api.saveIssue,
    method: 'post',
    data,
  });

export const updateOncall = (data) =>
  iAxios.request({
    url: api.updateOncall,
    method: 'post',
    data,
  });

export const exportFeedbacks = (data: apiType.ExportFeedbacksReq) =>
  iAxios.request<apiType.ExportFeedbacksRsp>({
    url: api.exportFeedbacks,
    method: 'post',
    data,
  });

export const exportAIFeedbacks = (data) => {
  const cData = {
    ...data,
    version: 2,
    platform_id: 1,
    tab: 0,
    filters: [...(data?.filters || []), ...AI_REPLY_FIELD],
  };
  return iAxios.request({
    url: api.exportFeedbacks,
    method: 'post',
    data: cData,
  });
};

export const getTagCountList = (data) =>
  iAxios.request({
    url: api.getTagCountList,
    method: 'post',
    data,
  });

export const unbindTapd = (data) =>
  iAxios.request({
    url: api.unbindTapd,
    method: 'post',
    data,
  });

export const sendMessage = (data) =>
  iAxios.request({
    url: api.sendMessage,
    method: 'post',
    data,
  });

export const moveApp = (data) =>
  iAxios.request({
    url: api.moveApp,
    method: 'post',
    data,
  });

// 提交反馈
export const submitfeedback = (data) =>
  iAxios.request({
    url: api.submitfeedback,
    method: 'post',
    data,
  });

export const importfeedback = (data) =>
  iAxios.request({
    url: api.importfeedback,
    method: 'post',
    data,
  });

export const getFeedbackUserTrace = (data) =>
  iAxios.request({
    url: api.getFeedbackUserTrace,
    method: 'post',
    data,
    requireFieldCheck: {
      fields: ['feedback_id'],
      return: {
        curr_month_total: 0,
        feedbacks: [],
        last_feedback_time: '0',
        one_year_seq: 0,
        one_year_total: 0,
      },
    },
  });

export const getAiReply = (params) =>
  iAxios.request({
    url: api.genReply,
    method: 'get',
    params,
  });

export const getAppRegisterRecords = (data: apiType.GetAuditDataParams) =>
  iAxios.request<apiType.AuditResponse>({
    url: api.getAppRegisterRecords,
    method: 'post',
    data,
  });

export const auditAppRecord = (data: apiType.AuditParams) =>
  iAxios.request({
    url: api.auditApp,
    method: 'post',
    data,
  });

// 回复数据上报 tpilot
export const tpilotReportUsage = (data) =>
  iAxios.request({
    url: api.tpilotReportUsage,
    method: 'post',
    data,
  });

/** 发送ai报告 */
export const reportEmail = (data: apiType.ReportEmailReq) =>
  iAxios.request({
    url: api.reportEmail,
    method: 'post',
    data,
  });

/** ai报告详情 */
export const reportDetail = (params) =>
  iAxios.request<apiType.ReportDetailRes>({
    url: api.reportDetail,
    method: 'get',
    params,
  });

/** 生成ai报告普通接口 */
export const aiCompontGenerate = (data: apiType.GenerateReportReq) =>
  iAxios.request<apiType.GenerateReportRes>({
    url: api.aiCompontGenerate,
    method: 'post',
    data,
  });

/** 生成ai报告流式接口 */
export const getAiCompontStream = (
  body: apiType.GenerateReportReq,
  onMessage: (data: any) => void,
  onClose: () => void,
  onError?: (err: any) => void,
  signal?: AbortSignal
) => {
  fetchEventSource(
    api.aiCompontStream,
    addCommonHeader({
      method: 'POST',
      body: JSON.stringify(body),
      signal,
      onclose() {
        onClose();
      },
      onmessage(event) {
        onMessage({ data: JSON.parse(event.data)?.data, cache: false });
      },
      onerror(err) {
        onError?.(err);
        // 抛出错误, 不再重试
        throw err;
      },
    })
  );
};

export const getUserRecentFeedbacks = (data: apiType.RecentFeedbackReq) =>
  iAxios.request<apiType.RecentFeedbackRes>({
    url: api.getUserRecentFeedbacks,
    method: 'post',
    data,
  });

export const getIclassDateTrend = (data: any) =>
  iAxios.request({
    url: api.getStatisticsDateData,
    method: 'post',
    data,
    requireFieldCheck: {
      fields: ['model_id'],
      check: (data) => data?.model_id?.length > 1,
      return: { statistics: [] },
    },
  });

export const createAnalyzeTask = (data?: apiType.CreateAnalyzeTaskReq) =>
  iAxios.request<apiType.CreateAnalyzeTaskRes>({
    url: api.createAnalyzeTask,
    method: 'post',
    data,
  });

export const getAnalyzeTask = (data?: apiType.GetAnalyzeTaskReq) =>
  iAxios.request<apiType.GetAnalyzeTaskRes>({
    url: api.getAnalyzeTask,
    method: 'post',
    data,
  });

export const checkCanDownload = (params?: apiType.CheckCanDownloadReq) =>
  iAxios.request<apiType.CheckCanDownloadRsp>({
    url: api.checkCanDownload,
    method: 'get',
    params,
  });

export const logFilterView = (data?: any) =>
  iAxios.request<string>({
    url: api.logFilterView,
    method: 'post',
    data,
  });

export const uploadFileToCos = (data?: any) =>
  iAxios.request<string>({
    url: api.uploadFileToCos,
    method: 'post',
    data,
  });

/** 附件上传历史 */
export const getUploadFileHistory = (data?: apiType.UploadHistoryReq) =>
  iAxios.request<apiType.UploadHistoryRsp>({
    url: api.getUploadFileHistory,
    method: 'post',
    data,
  });

/** 版本号筛选 */
export const versionFilter = (data?: any, params?: any) =>
  iAxios.request<string[]>({
    url: api.versionFilter,
    method: 'post',
    data,
    params,
  });

/** 引用场景搜索 */
export const logScenConfigSearch = (data) =>
  iAxios.request({
    url: api.logScenConfigSearch,
    method: 'post',
    data,
  });

/** 引用场景增加 */

export const logScenConfigAdd = (data?: apiType.SceneAddReq) =>
  iAxios.request({
    url: api.logScenConfigAdd,
    method: 'post',
    data,
  });

/** 引用场景修改 */
export const logScenConfigUpdate = (data?: apiType.SceneUpdateReq) =>
  iAxios.request({
    url: api.logScenConfigUpdate,
    method: 'post',
    data,
  });

/** 获取 tpilot 功能开关信息 */
export const tpilotConfigSwitch = (data?: apiType.TpilotConfigSwitchReq) =>
  iAxios.request<apiType.TpilotConfigSwitchRsp>({
    url: api.tpilotConfigSwitchs,
    method: 'post',
    data,
  });

export const getTpilotPresetQuestion = (params: {
  app_id: string;
  task_type: number;
  prompt_key?: string;
  question_text?: string;
  output_scene?: string;
}) =>
  iAxios.request<apiType.PresetQuestionItem[]>({
    url: api.getTpilotPresetQuestion,
    method: 'get',
    params,
  });

export const tpilotClusterCummary = (data?: {
  app_id: string;
  contents: string[];
}) =>
  iAxios.request<{ topic: string }>({
    url: api.tpilotClusterCummary,
    method: 'post',
    data,
  });

/** 添加预设问题 */
export const addTpilotPresetQuestion = (data: apiType.AddPresetQuestionReq) =>
  iAxios.request<apiType.PresetQuestionItem>({
    url: api.addTpilotPresetQuestion,
    method: 'post',
    data,
  });

/** 更新预设问题 */
export const updateTpilotPresetQuestion = (
  data: apiType.UpdatePresetQuestionReq
) =>
  iAxios.request<apiType.PresetQuestionItem>({
    url: api.updateTpilotPresetQuestion,
    method: 'post',
    data,
  });

/** 删除预设问题 */
export const deleteTpilotPresetQuestion = (
  data: apiType.DeletePresetQuestionReq
) =>
  iAxios.request<apiType.PresetQuestionItem>({
    url: api.deleteTpilotPresetQuestion,
    method: 'post',
    data,
  });

/** 获取旧模型开关标签 */
export const getOldModelTag = (params: { app_id: string }) =>
  iAxios.request<{ enabled: boolean }>({
    url: api.getOldModelTag,
    method: 'get',
    params,
  });

/** 设置旧模型开关标签 */
export const setOldModelTag = (data: { app_id: string; enabled: boolean }) =>
  iAxios.request<{ success: boolean }>({
    url: api.setOldModelTag,
    method: 'post',
    data,
  });
